import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Clock, ChevronDown, X } from 'lucide-react';

const CalendarFilter = ({
  filters,
  onFilterChange,
  className = ""
}) => {
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('custom');
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsPresetOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Date calculation helper functions
  const getDateString = (d) => {
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts.map(Number);
    // Use local Date constructor which treats values as local
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString();
  };

  const getToday = () => new Date();

  const getStartOfWeek = (date = new Date()) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
    return new Date(start.setDate(diff));
  };

  const getEndOfWeek = (date = new Date()) => {
    const end = getStartOfWeek(date);
    end.setDate(end.getDate() + 6);
    return end;
  };

  const getStartOfMonth = (date = new Date()) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getEndOfMonth = (date = new Date()) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const getStartOfYear = (date = new Date()) => {
    return new Date(date.getFullYear(), 0, 1);
  };

  const getEndOfYear = (date = new Date()) => {
    return new Date(date.getFullYear(), 11, 31);
  };

  // Predefined period options
  const presetOptions = [
    {
      value: 'today',
      label: 'Today',
      icon: '📅',
      getDates: () => {
        const today = getToday();
        return { start: getDateString(today), end: getDateString(today) };
      }
    },
    {
      value: 'yesterday',
      label: 'Yesterday',
      icon: '⏮️',
      getDates: () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: getDateString(yesterday), end: getDateString(yesterday) };
      }
    },
    {
      value: 'this-week',
      label: 'This Week',
      icon: '📆',
      getDates: () => {
        const startOfWeek = getStartOfWeek();
        const endOfWeek = getEndOfWeek();
        return { start: getDateString(startOfWeek), end: getDateString(endOfWeek) };
      }
    },
    {
      value: 'last-week',
      label: 'Last Week',
      icon: '📋',
      getDates: () => {
        const lastWeekStart = getStartOfWeek();
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = getEndOfWeek(lastWeekStart);
        return { start: getDateString(lastWeekStart), end: getDateString(lastWeekEnd) };
      }
    },
    {
      value: 'this-month',
      label: 'This Month',
      icon: '🗓️',
      getDates: () => {
        const startOfMonth = getStartOfMonth();
        const endOfMonth = getEndOfMonth();
        return { start: getDateString(startOfMonth), end: getDateString(endOfMonth) };
      }
    },
    {
      value: 'last-month',
      label: 'Last Month',
      icon: '📊',
      getDates: () => {
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        const startOfLastMonth = getStartOfMonth(lastMonth);
        const endOfLastMonth = getEndOfMonth(lastMonth);
        return { start: getDateString(startOfLastMonth), end: getDateString(endOfLastMonth) };
      }
    },
    {
      value: 'this-quarter',
      label: 'This Quarter',
      icon: '📈',
      getDates: () => {
        const now = new Date();
        const quarter = Math.floor((now.getMonth() + 3) / 3);
        const startOfQuarter = new Date(now.getFullYear(), (quarter - 1) * 3, 1);
        const endOfQuarter = new Date(now.getFullYear(), quarter * 3, 0);
        return { start: getDateString(startOfQuarter), end: getDateString(endOfQuarter) };
      }
    },
    {
      value: 'last-quarter',
      label: 'Last Quarter',
      icon: '📉',
      getDates: () => {
        const now = new Date();
        const currentQuarter = Math.floor((now.getMonth() + 3) / 3);
        const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
        const year = currentQuarter === 1 ? now.getFullYear() - 1 : now.getFullYear();
        const startOfLastQuarter = new Date(year, (lastQuarter - 1) * 3, 1);
        const endOfLastQuarter = new Date(year, lastQuarter * 3, 0);
        return { start: getDateString(startOfLastQuarter), end: getDateString(endOfLastQuarter) };
      }
    },
    {
      value: 'this-year',
      label: 'This Year',
      icon: '🎯',
      getDates: () => {
        const startOfYear = getStartOfYear();
        const endOfYear = getEndOfYear();
        return { start: getDateString(startOfYear), end: getDateString(endOfYear) };
      }
    },
    {
      value: 'last-year',
      label: 'Last Year',
      icon: '📜',
      getDates: () => {
        const lastYear = new Date();
        lastYear.setFullYear(lastYear.getFullYear() - 1);
        const startOfLastYear = getStartOfYear(lastYear);
        const endOfLastYear = getEndOfYear(lastYear);
        return { start: getDateString(startOfLastYear), end: getDateString(endOfLastYear) };
      }
    },
    {
      value: 'last-7-days',
      label: 'Last 7 Days',
      icon: '🔄',
      getDates: () => {
        const end = getToday();
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        return { start: getDateString(start), end: getDateString(end) };
      }
    },
    {
      value: 'last-30-days',
      label: 'Last 30 Days',
      icon: '📅',
      getDates: () => {
        const end = getToday();
        const start = new Date(end);
        start.setDate(start.getDate() - 29);
        return { start: getDateString(start), end: getDateString(end) };
      }
    },
    {
      value: 'last-90-days',
      label: 'Last 90 Days',
      icon: '📋',
      getDates: () => {
        const end = getToday();
        const start = new Date(end);
        start.setDate(start.getDate() - 89);
        return { start: getDateString(start), end: getDateString(end) };
      }
    }
  ];

  // Handle preset selection
  const handlePresetSelect = (preset) => {
    if (preset.value === 'custom') {
      setSelectedPreset('custom');
      setIsPresetOpen(false);
      return;
    }

    const dates = preset.getDates();
    onFilterChange('startDate', dates.start);
    onFilterChange('endDate', dates.end);
    setSelectedPreset(preset.value);
    setIsPresetOpen(false);
  };

  // Handle manual date change
  const handleDateChange = (field, value) => {
    onFilterChange(field, value);
    setSelectedPreset('custom');
  };

  // Clear dates
  const clearDates = () => {
    onFilterChange('startDate', '');
    onFilterChange('endDate', '');
    setSelectedPreset('custom');
  };

  // Get current preset label
  const getCurrentPresetLabel = () => {
    if (selectedPreset === 'custom') return 'Custom Range';
    const preset = presetOptions.find(p => p.value === selectedPreset);
    return preset ? preset.label : 'Custom Range';
  };

  // Check if current dates match a preset
  useEffect(() => {
    if (!filters.startDate || !filters.endDate) {
      setSelectedPreset('custom');
      return;
    }

    const matchingPreset = presetOptions.find(preset => {
      const dates = preset.getDates();
      return dates.start === filters.startDate && dates.end === filters.endDate;
    });

    setSelectedPreset(matchingPreset ? matchingPreset.value : 'custom');
  }, [filters.startDate, filters.endDate]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Preset Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          <Clock className="w-4 h-4 inline mr-1" />
          Quick Select Period
        </label>

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsPresetOpen(!isPresetOpen)}
            className="w-full px-4 py-2 text-left border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white dark:bg-slate-900 border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-100"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-slate-200">
                {getCurrentPresetLabel()}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isPresetOpen ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {isPresetOpen && (
            <div className="absolute z-10 w-full mt-1 border rounded-md shadow-lg max-h-80 overflow-y-auto bg-white dark:bg-slate-800 border-gray-300 dark:border-white/20">
              {/* Custom option */}
              <button
                onClick={() => handlePresetSelect({ value: 'custom', label: 'Custom Range' })}
                
                className={`w-full px-4 py-2 text-left text-sm ${
                  selectedPreset === 'custom'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <span className="mr-2">⚙️</span>
                Custom Range
              </button>

              {/* Divider */}
              <div className="border-t my-1 border-gray-200 dark:border-white/10"></div>
              
              {/* Preset options */}
              {presetOptions.map(preset => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetSelect(preset)}
                  className={`w-full px-4 py-2 text-left text-sm ${
                    selectedPreset === preset.value
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <span className="mr-2">{preset.icon}</span>
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Manual Date Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          <Calendar className="w-4 h-4 inline mr-1" />
          Date Range
        </label>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm border-gray-300 dark:bg-slate-900 dark:border-white/20 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm border-gray-300 dark:bg-slate-900 dark:border-white/20 dark:text-slate-100"
            />
          </div>
        </div>
      </div>

      {/* Selected Range Display */}
      {filters.startDate && filters.endDate && (
        <div className="p-3 rounded-md border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
                Selected Range: {getCurrentPresetLabel()}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">
                {new Date(filters.startDate).toLocaleDateString()} - {new Date(filters.endDate).toLocaleDateString()}
              </div>
            </div>
            <button
              onClick={clearDates}
              className="p-1 rounded transition-colors text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarFilter;